import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import {
	getInternalEmailDomainErrorMessage,
	isAllowedInternalEmail,
} from "@/lib/internal-email-domain";
import {
	buildRequiredTwoFactorSetupUrl,
	getSafePostAuthRedirectTarget,
	hasTwoFactorEnabled,
} from "@/lib/two-factor-policy";

const LOGO_URL =
	"https://cdn.prod.website-files.com/64493d11d62bbdf4ac3b43ef/644965713d613f2767b8f9fa_5da4ec2b220488c9b249d5dc_Anthem%20Logo_sq-01.webp";

export default function LoginPage() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	const [email, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const urlError = searchParams.get("error");
	const redirectTarget = React.useMemo(
		() => getSafePostAuthRedirectTarget(searchParams.get("redirect")),
		[searchParams],
	);
	const displayError =
		urlError === "unauthorized"
			? "Access denied. You do not have permission to access this application."
			: urlError === "invalid-domain"
				? getInternalEmailDomainErrorMessage()
				: error;

	React.useEffect(() => {
		if (urlError === "unauthorized" || urlError === "invalid-domain") {
			void authClient.signOut();
		}
	}, [urlError]);

	const handleSubmit = React.useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			setError(null);

			if (!email || !password) {
				setError("Please fill in all fields");
				return;
			}

			const normalizedEmail = email.trim().toLowerCase();
			if (!isAllowedInternalEmail(normalizedEmail)) {
				setError(getInternalEmailDomainErrorMessage());
				return;
			}

			setLoading(true);
			try {
				const result = await authClient.signIn.email({
					email: normalizedEmail,
					password,
				});

				if (result.error) {
					setError(result.error.message || "Invalid email or password");
					setLoading(false);
					return;
				}

				if (
					(result as { data?: { twoFactorRedirect?: boolean } }).data
						?.twoFactorRedirect
				) {
					navigate(
						`/verify-2fa?redirect=${encodeURIComponent(redirectTarget)}`,
					);
					return;
				}

				const signedInUser = (
					result as {
						data?: { user?: { twoFactorEnabled?: boolean | null } };
					}
				).data?.user;

				if (!hasTwoFactorEnabled(signedInUser)) {
					navigate(buildRequiredTwoFactorSetupUrl(redirectTarget));
					return;
				}

				navigate(redirectTarget);
			} catch (submitError) {
				console.error("[LOGIN] Sign in exception:", submitError);
				setError("An error occurred during sign in");
				setLoading(false);
			}
		},
		[email, password, navigate, redirectTarget],
	);

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="absolute top-4 right-4">
				<ThemeToggle />
			</div>
			<Card className="w-full max-w-md">
				<CardHeader className="space-y-1">
					<div className="mb-4 flex justify-center">
						<img src={LOGO_URL} alt="Cherry" className="h-24" />
					</div>
					<CardTitle className="text-center text-2xl">Cherry</CardTitle>
					<CardDescription className="text-center">
						Sign in to Cherry
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit} className="space-y-4">
						{displayError && (
							<div className="rounded-md bg-destructive/15 p-3 text-center text-sm text-destructive">
								{displayError}
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								name="email"
								type="email"
								placeholder="you@example.com"
								value={email}
								onChange={(event) => setEmail(event.target.value)}
								required
								disabled={loading}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								name="password"
								type="password"
								placeholder="••••••••"
								value={password}
								onChange={(event) => setPassword(event.target.value)}
								required
								disabled={loading}
							/>
						</div>

						<Button type="submit" disabled={loading} className="w-full">
							{loading ? "Signing in..." : "Sign In"}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
